import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import { Text } from "@/components/podkit/typography/Text";
import { DeleteSecretModal } from "@/components/secrets/DeleteSecretModal";
import { EditSecretModal } from "@/components/secrets/EditSecretModal";
import { timeAgo } from "@/format/time";
import { useMembers } from "@/queries/organization-queries";
import { type PlainSecret } from "@/queries/secret-queries";
import { Timestamp } from "@bufbuild/protobuf";
import { useMemo, useState, type FC } from "react";

type Props = {
    secrets: PlainSecret[];
    showCreatorColumn?: boolean;
    canUpdateSecret?: boolean;
    canDeleteSecret?: boolean;
};

export const SecretsTable: FC<Props> = ({
    secrets,
    showCreatorColumn = true,
    canUpdateSecret = true,
    canDeleteSecret = true,
}) => {
    const [secretToDelete, setSecretToDelete] = useState<PlainSecret | null>(null);
    const [secretToEdit, setSecretToEdit] = useState<PlainSecret | null>(null);

    const sorted = useMemo(() => {
        return [...secrets].sort((a, b) => {
            const aName = a.name.toLowerCase();
            const bName = b.name.toLowerCase();
            return aName.localeCompare(bName);
        });
    }, [secrets]);

    return (
        <>
            <Table data-testid="secrets-table">
                <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                    <TableRow className="h-8 text-base text-content-strong">
                        <TableHead className="pl-3">Name</TableHead>
                        <TableHead className="pl-3">Type</TableHead>
                        {showCreatorColumn && <TableHead className="pl-3">Created by</TableHead>}
                        <TableHead className="pl-3">Last updated</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody className="border-t border-border-light text-base">
                    {sorted.map((secret) => (
                        <SecretRow
                            key={secret.id}
                            secret={secret}
                            onDelete={(secret) => setSecretToDelete(secret)}
                            onEdit={(secret) => setSecretToEdit(secret)}
                            showCreatorColumn={showCreatorColumn}
                            canUpdateSecret={canUpdateSecret}
                            canDeleteSecret={canDeleteSecret}
                        />
                    ))}
                </TableBody>
            </Table>

            {secretToDelete && <DeleteSecretModal secret={secretToDelete} onClose={() => setSecretToDelete(null)} />}
            {secretToEdit && <EditSecretModal secret={secretToEdit} onClose={() => setSecretToEdit(null)} />}
        </>
    );
};

const SecretRow: FC<{
    secret: PlainSecret;
    onDelete: (secret: PlainSecret) => void;
    onEdit: (secret: PlainSecret) => void;
    showCreatorColumn?: boolean;
    canUpdateSecret?: boolean;
    canDeleteSecret?: boolean;
}> = ({ secret, onDelete, onEdit, showCreatorColumn = true, canUpdateSecret = true, canDeleteSecret = true }) => {
    const { data: members } = useMembers();

    const lastUpdatedDate = useMemo(() => {
        const ts = secret.updatedAt || secret.createdAt;
        if (!ts) {
            return;
        }
        return new Timestamp(ts).toDate();
    }, [secret.updatedAt, secret.createdAt]);

    const lookup = useMemo(() => {
        return Object.fromEntries(members?.members.map((m) => [m.userId, m]) || []);
    }, [members?.members]);

    return (
        <TableRow className="h-16 border-border-light" data-testid={secret.id}>
            <TableCell>
                <Text className="truncate font-mono text-base font-bold text-content-primary">{secret.name}</Text>
            </TableCell>
            <TableCell>
                <Text className="truncate text-base">
                    {(() => {
                        switch (secret.mount.case) {
                            case "environmentVariable":
                                return "Environment Variable";
                            case "filePath":
                                return "File";
                            case "containerRegistryBasicAuthHost":
                                return "Container Registry Basic Auth";
                            case "apiOnly":
                                return "API Only";
                            default:
                                return "";
                        }
                    })()}
                </Text>
            </TableCell>
            {showCreatorColumn && (
                <TableCell>
                    <Text className="truncate text-base">
                        {lookup[secret.creator?.id || ""]?.fullName || (
                            <span className="text-content-secondary">--</span>
                        )}
                    </Text>
                </TableCell>
            )}
            <TableCell>
                {lastUpdatedDate ? (
                    <Text className="truncate text-base" title={lastUpdatedDate.toISOString()}>
                        {timeAgo(lastUpdatedDate)}
                    </Text>
                ) : (
                    <span className="text-content-secondary">--</span>
                )}
            </TableCell>
            <TableCell className="w-8">
                <div className="flex items-center justify-end gap-2">
                    <Button
                        data-testid={`edit-secret-${secret.id}-button`}
                        variant="secondary"
                        size="sm"
                        onClick={() => onEdit(secret)}
                        className={cn({ hidden: !canUpdateSecret })}
                        data-tracking-id="edit-secret-secrets-table"
                    >
                        Edit
                    </Button>
                    <Button
                        data-testid={`delete-secret-${secret.id}-button`}
                        LeadingIcon={IconTrash}
                        variant="ghost"
                        size="sm"
                        className={cn("border-0 px-1", { hidden: !canDeleteSecret })}
                        onClick={() => onDelete(secret)}
                        aria-label="Delete secret"
                        data-tracking-id="delete-secret-secrets-table"
                    />
                </div>
            </TableCell>
        </TableRow>
    );
};
